var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSiteSettings } from '../context/SiteSettingsContext';
import MaintenancePage from '../pages/MaintenancePage';
import { hasManagerWriteSession } from '../utils/managerSession';
// Routes that should always be accessible even during maintenance (for login flow)
var AUTH_ROUTES = ['/signin', '/login', '/callback'];
// Routes accessible to managers during maintenance (when allowManagerAccess is true)
var MANAGER_ROUTES = ['/spot-changes'];
/**
 * MaintenanceWrapper
 *
 * Wraps the app content and shows maintenance page when maintenance mode is enabled.
 * - Admins can bypass maintenance mode entirely if allowAdminAccess is true.
 * - Managers can ONLY access /spot-changes during maintenance (not public site).
 * - Auth routes (/login, /callback) are always accessible to allow login.
 */
var MaintenanceWrapper = function (_a) {
    var _b, _c;
    var children = _a.children;
    var _d = useSiteSettings(), settings = _d.settings, loading = _d.loading, isMaintenanceMode = _d.isMaintenanceMode, canBypassMaintenance = _d.canBypassMaintenance;
    var location = useLocation();
    var navigate = useNavigate();
    // Check if user has an active manager session
    var _e = useState(false), hasManagerSession = _e[0], setHasManagerSession = _e[1];
    useEffect(function () {
        setHasManagerSession(hasManagerWriteSession());
    }, [location.pathname]); // Re-check on route change
    // Check current route
    var currentPath = location.pathname;
    var isAuthRoute = AUTH_ROUTES.some(function (route) { return currentPath.startsWith(route); });
    var isManagerRoute = MANAGER_ROUTES.some(function (route) { return currentPath.startsWith(route); });
    // Check if manager access is allowed (standalone setting)
    var isManagerAccessAllowed = (settings === null || settings === void 0 ? void 0 : settings.allowManagerAccess) !== false;
    // Redirect managers away from non-manager routes during maintenance
    useEffect(function () {
        if (isMaintenanceMode && hasManagerSession && !canBypassMaintenance && !isAuthRoute && !isManagerRoute) {
            // Manager is trying to access public site during maintenance - redirect to their dashboard
            if (isManagerAccessAllowed) {
                navigate('/spot-changes', { replace: true });
            }
            // If manager access is revoked, they'll see maintenance page (handled below)
        }
    }, [isMaintenanceMode, hasManagerSession, canBypassMaintenance, isAuthRoute, isManagerRoute, isManagerAccessAllowed, navigate]);
    // Show loading while fetching settings
    // EXCEPTION: Don't show loading spinner for manager routes - they have their own PIN auth flow
    // Showing loading here would unmount the PIN gate and lose state
    if (loading && !isManagerRoute) {
        return (_jsx("div", __assign({ className: "fixed inset-0 bg-[#1a1a2e] flex items-center justify-center z-50" }, { children: _jsxs("div", __assign({ className: "text-center" }, { children: [_jsx("div", { className: "animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--accent-500)] mx-auto mb-3" }), _jsx("p", __assign({ className: "text-[#b8b8b8] text-sm" }, { children: "Loading..." }))] })) })));
    }
    // Allow through if:
    // 1. Not in maintenance mode
    // 2. Admin who can bypass
    // 3. On auth route (login/callback)
    // 4. Manager on their allowed route (with access enabled)
    var isManagerWithAccess = hasManagerSession && isManagerAccessAllowed && isManagerRoute;
    if (!isMaintenanceMode || canBypassMaintenance || isAuthRoute || isManagerWithAccess) {
        return _jsx(_Fragment, { children: children });
    }
    // Everyone else sees maintenance page
    return (_jsx(MaintenancePage, { message: (_b = settings === null || settings === void 0 ? void 0 : settings.maintenanceMode) === null || _b === void 0 ? void 0 : _b.message, estimatedEndTime: (_c = settings === null || settings === void 0 ? void 0 : settings.maintenanceMode) === null || _c === void 0 ? void 0 : _c.estimatedEndTime }));
};
export default MaintenanceWrapper;
